import _debounce from 'lodash/debounce';
import { isElementVisible } from 'lib/ui';
import { adsRefreshed, adsInitialized } from './actions';
import getMergedAdConfig from './util/getMergedAdConfig';

const breakpoints = {
    'desktop-lg': 'xl',
    desktop: 'l',
    tablet: 'm',
    mobile: 's'
};

const AdType = {
    WALLPAPER: 'wallpaper',
    OVERLAY: 'overlay'
};

class AdManager {
    constructor(props) {
        this.props = props;
        this._onResize = _debounce(this._onResize.bind(this), 100);
        // debounced to handle multiple late ads that's added at the same timing in one shot
        this._onRegister = _debounce(this._refreshAll.bind(this), 4);
    }

    set adManager(value) {
        this._adManager = value;
    }

    get gptSlots() {
        return (this._adManager && this._adManager.getGPTArray({ independent: false })) || [];
    }

    get hasAds() {
        return this.getMountedInstances().length > 0;
    }

    get hasOutOfPageAd() {
        return this.gptSlots.filter(slot => slot.type === AdType.OVERLAY).length > 0;
    }

    getMountedInstances() {
        if (!this.mountedInstances) {
            this.mountedInstances = [];
        }
        return this.mountedInstances;
    }

    addInstance(instance) {
        const instances = this.getMountedInstances();
        const index = instances.indexOf(instance);
        if (index === -1) {
            // The first instance starts listening for the event.
            if (instances.length === 0) {
                this._toggleListener(true);
            }
            instances.push(instance);
        }
        // register ad individually when they are added after the page load/route change.
        // this will get called earlier than a page request is made which invalidates `this.props.ads.initialized`,
        // so need to check the flag `this._initializing` to make sure this is called after the ad initialization is done
        if (!this._initializing && this.props.ads.initialized) {
            this._registerAd(instance);
        }
    }

    removeInstance(instance) {
        const instances = this.getMountedInstances();
        const index = instances.indexOf(instance);
        if (index >= 0) {
            instances.splice(index, 1);
            // The last instance removes listening for the event.
            if (instances.length === 0) {
                this._toggleListener(false);
            }
        }
    }

    preInit() {
        this._initializing = true;
    }

    initAds(props) {
        if (!this._adManager || !this.hasAds) {
            this._initializing = false;
            return;
        }

        this.props = props;

        const { ads = {}, dispatch } = props;
        if (ads.id && ads.configured && (!ads.initialized || ads.refresh)) {
            try {
                this._adConfig = getMergedAdConfig(props);
                this._adManager.init(this._adConfig);
                this._initializing = false;
                dispatch(adsInitialized());
                // add refreshing ads in the queue to ensure it gets called after the ad initialization is complete
                // which is also enqueued
                window.googletag.cmd.push(() => {
                    this._refreshAll();
                });
            } catch (err) {
                console.error(err); // eslint-disable-line
            }
        }
    }

    onMediaQueryChange(props) {
        this.props = props;
        this._refreshAds();
    }

    _toggleListener(add) {
        ['scroll', 'resize'].forEach(eventName => {
            window[add ? 'addEventListener' : 'removeEventListener'](eventName, this._onResize);
        });
    }

    _registerAd(instance) {
        if (!this._adManager) {
            return;
        }
        const { refs: { adSlot } } = instance;
        this._adManager.registerSlot(adSlot, this._adConfig);
        this._onRegister();
    }

    _refreshAll() {
        // check the element visibility first to update component state so that they will be reflected in `refreshAds`
        this._checkElementVisibility(() => {
            this._refreshAds();
        });
    }

    _refreshAds(instances = this.getMountedInstances()) {
        if (!this._adManager) {
            return;
        }
        const { dispatch } = this.props;
        const slotsToClear = [];
        const slotsToRefresh = this.gptSlots.filter((gptSlot) => {
            let shouldRefresh = false;
            // wallpaper ad is added by a page shell, mark as refreshable
            if (gptSlot.type === AdType.WALLPAPER) {
                shouldRefresh = true;
            } else {
                const activeAdInstance = instances.filter(({ refs: { adSlot } }) => adSlot.id === gptSlot.getSlotElementId())[0];
                if (activeAdInstance) {
                    const { state: { refreshed }, props: { refreshWhenVisible }, refs: { adSlot } } = activeAdInstance;
                    // either a non-lazy ad or a lazy ad which has been already refreshed
                    const isRefreshable = !refreshWhenVisible || (refreshWhenVisible && refreshed);
                    const isIncludedInBreakpoint = this._shouldShowSlotForBreakpoint(adSlot);
                    shouldRefresh = isRefreshable && isIncludedInBreakpoint;
                    if (!shouldRefresh) {
                        slotsToClear.push(gptSlot);
                    }
                    gptSlot.refreshable = shouldRefresh;
                }
            }
            // this flag is used in the patched `getGPTArray` in `connectAdInitializer` to filter out non-refreshable ads
            gptSlot.refreshable = shouldRefresh;

            return shouldRefresh;
        });

        if (slotsToClear.length > 0) {
            // FIXME: this._adManager.clear() doesn't accept an array of slots
            window.googletag.cmd.push(() => {
                window.googletag.pubads().clear(slotsToClear);
            });
        }
        // when there is an out-of-page ad in the page, it needs to render an out-of-page ad first followed by the rest of ads
        // https://code.espn.com/webdev/score/blob/develop/static/js/ads/admgr.js#L145-L149
        // https://code.espn.com/webdev/score/blob/develop/static/js/ads/init.js#L341-L368
        if (this.hasOutOfPageAd) {
            this._adManager.refreshOutOfPageAdSlots();
        } else if (slotsToRefresh.length > 0) {
            this._adManager.refresh(slotsToRefresh);
        }
        dispatch(adsRefreshed());
    }

    _onResize() {
        this._checkElementVisibility((instances) => {
            if (instances) { // call `refreshAds` only when there are instances to refresh
                // use this logic when it's ok to just refresh the ad which just gets visible
                // this._refreshAds(instances);

                // use this logic so that we can ensure competitive exclusion in a page
                this._refreshAds();
            }
        });
    }

    _checkElementVisibility(callback) {
        // instances set to lazy-render and not rendered yet
        const instancesToCheck = this.getMountedInstances()
            .filter(({ state: { refreshed }, props: { refreshWhenVisible } }) => refreshWhenVisible && !refreshed);

        if (instancesToCheck.length > 0) {
            const visibleInstances = instancesToCheck
                .filter(({ refs: { adSlot } }) => isElementVisible(adSlot));

            if (visibleInstances.length > 0) {
                visibleInstances.forEach((instance, index, arr) => {
                    instance.setState({ refreshed: true }, index === arr.length - 1 ? () => {
                        callback(visibleInstances);
                    } : null);
                });
                return;
            }
        }

        if (callback) {
            callback();
        }
    }

    // logic copied from https://code.espn.com/webdev/score/blob/develop/static/js/ads/admgr.js#L664
    _shouldShowSlotForBreakpoint(slot) {
        const bp = breakpoints[this.props.mq];
        const include = slot.getAttribute('data-include-bp');
        const exclude = slot.getAttribute('data-exclude-bp');
        let shouldShow = true;

        if (include) {
            // if the current breakpoint is not found in the included array, skip this slot
            if (include.split(',').indexOf(bp) === -1) {
                shouldShow = false;
            }
        }

        if (exclude) {
            // if the current breakpoint is found in the exluded breakpoints array, skip this slot
            if (exclude.split(',').indexOf(bp) !== -1) {
                shouldShow = false;
            }
        }

        return shouldShow;
    }
}

export default AdManager;



// WEBPACK FOOTER //
// ./app/common/ads/AdManager.js